import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sun, CheckSquare, AlertTriangle, TrendingUp, Clock, DollarSign, Loader2, Plus, Phone, StickyNote, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const fmtCur = (n) => n == null ? '—' : `$${parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const MyDay = ({ onSelectDeal, onOpenNewDeal }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ax.get('/api/admin/my-day');
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 size={28} className="animate-spin" style={{ color: 'var(--teal)' }} /></div>;
  if (!data) return null;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <div data-testid="my-day">
      {/* Greeting header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <Sun size={26} style={{ color: '#F59E0B' }} />
            {greeting}, {data.user?.name?.split(' ')[0] || 'there'}
          </h2>
          <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: '2px 0 0' }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onOpenNewDeal} style={quickBtn} data-testid="my-day-add-deal">
            <Plus size={14} /> Add Deal
          </button>
        </div>
      </div>

      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <StatCard icon={<CheckSquare size={16} />} label="Today's Tasks" value={data.today_tasks.length} sub={data.overdue_count > 0 ? `${data.overdue_count} overdue` : 'On track'} color={data.overdue_count > 0 ? '#DC2626' : '#10B981'} testId="stat-tasks" />
        <StatCard icon={<TrendingUp size={16} />} label="My Open Deals" value={data.my_deals_count} sub={`${data.hot_deals.length} hot`} color="#3B82F6" testId="stat-deals" />
        <StatCard icon={<AlertTriangle size={16} />} label="Stale Deals" value={data.stale_deals.length} sub="Need follow-up" color="#F59E0B" testId="stat-stale" />
        {data.mtd_commission != null && (
          <StatCard icon={<DollarSign size={16} />} label="MTD Commission" value={fmtCur(data.mtd_commission)} sub={`${data.won_this_month_count} won`} color="#10B981" testId="stat-commission" isText />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Today's tasks */}
        <Section title="Today's Tasks" icon={<CheckSquare size={16} style={{ color: 'var(--teal)' }} />} count={data.today_tasks.length}>
          {data.today_tasks.length === 0 ? (
            <Empty text="No tasks due today. Nice." />
          ) : (
            data.today_tasks.slice(0, 8).map((t) => (
              <div key={t.id} style={rowItem} data-testid={`task-item-${t.id}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)' }}>{t.title}</div>
                  <div style={{ fontSize: '0.75rem', color: t.is_overdue ? '#DC2626' : '#6B7280' }}>
                    {t.is_overdue ? 'Overdue • ' : ''}Due {t.due_date ? new Date(t.due_date).toLocaleDateString() : '—'}
                  </div>
                </div>
                {t.deal_id && (
                  <button onClick={() => onSelectDeal?.(t.deal_id)} style={pillBtn}>Open</button>
                )}
              </div>
            ))
          )}
        </Section>

        {/* Hot deals */}
        <Section title="Hot Deals" icon={<TrendingUp size={16} style={{ color: '#EF4444' }} />} count={data.hot_deals.length}>
          {data.hot_deals.length === 0 ? (
            <Empty text="No hot deals in Proposal/Negotiation stage yet." />
          ) : (
            data.hot_deals.slice(0, 8).map((d) => (
              <div key={d.id} style={rowItem} onClick={() => onSelectDeal?.(d.id)} data-testid={`hot-deal-${d.id}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)' }}>{d.name} <span style={{ fontWeight: 400, color: '#6B7280' }}>• {d.company || 'no company'}</span></div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{d.stage} • {fmtCur(d.deal_value)}</div>
                </div>
                <span style={{ ...pillBtn, background: '#FEE2E2', color: '#991B1B' }}>{d.stage}</span>
              </div>
            ))
          )}
        </Section>

        {/* Stale deals */}
        <Section title="Stale Deals (7+ days)" icon={<Clock size={16} style={{ color: '#F59E0B' }} />} count={data.stale_deals.length}>
          {data.stale_deals.length === 0 ? (
            <Empty text="No stale deals — you're on top of it." />
          ) : (
            data.stale_deals.slice(0, 8).map((d) => (
              <div key={d.id} style={rowItem} onClick={() => onSelectDeal?.(d.id)} data-testid={`stale-deal-${d.id}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)' }}>{d.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>Last update: {d.updated_at ? new Date(d.updated_at).toLocaleDateString() : 'never'}</div>
                </div>
                <span style={{ ...pillBtn, background: '#FEF3C7', color: '#92400E' }}>{d.stage}</span>
              </div>
            ))
          )}
        </Section>

        {/* Recent activity */}
        <Section title="Recent Activity" icon={<Clock size={16} style={{ color: '#6B7280' }} />} count={data.recent_activity.length}>
          {data.recent_activity.length === 0 ? (
            <Empty text="Nothing yet — go make it happen." />
          ) : (
            data.recent_activity.slice(0, 8).map((a) => (
              <div key={a.id} style={{ ...rowItem, cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.813rem', color: 'var(--navy)' }}>{a.description}</div>
                  <div style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</div>
                </div>
              </div>
            ))
          )}
        </Section>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, color, testId, isText }) => (
  <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '0.875rem 1rem' }} data-testid={testId}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, color, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
      {icon}<span style={{ color: '#6B7280' }}>{label}</span>
    </div>
    <div style={{ fontFamily: 'var(--font-heading)', fontSize: isText ? '1.25rem' : '1.5rem', fontWeight: 700, color: 'var(--navy)' }}>{value}</div>
    {sub && <div style={{ fontSize: '0.75rem', color }}>{sub}</div>}
  </div>
);

const Section = ({ title, icon, count, children }) => (
  <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
    <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
      {icon}
      <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--navy)' }}>{title}</span>
      <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6B7280', background: '#F3F4F6', padding: '2px 8px', borderRadius: 100 }}>{count}</span>
    </div>
    <div style={{ maxHeight: 320, overflowY: 'auto' }}>{children}</div>
  </div>
);

const Empty = ({ text }) => (
  <div style={{ padding: '1.25rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.813rem' }}>{text}</div>
);

const rowItem = { display: 'flex', alignItems: 'center', gap: 8, padding: '0.625rem 1rem', borderBottom: '1px solid #F9FAFB', cursor: 'pointer', transition: 'background 0.15s' };
const pillBtn = { padding: '2px 8px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 600, background: '#F3F4F6', color: '#374151', border: 'none', cursor: 'pointer' };
const quickBtn = { padding: '0.5rem 0.875rem', background: 'var(--teal)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.813rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 };
